import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './QuantDashboard.css';

function Sparkline({ points, width = 280, height = 48 }) {
  if (!points?.length) return <div className="qd-spark-empty">No history yet</div>;
  const vals = points.map((p) => p.cum);
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 1);
  const range = max - min || 1;
  const coords = vals.map((v, i) => {
    const x = (i / Math.max(1, vals.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  const last = vals[vals.length - 1];
  return (
    <svg className="qd-spark" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <polyline points={coords} fill="none" stroke={last >= 0 ? '#4ade80' : '#f87171'} strokeWidth="2" />
    </svg>
  );
}

function SignalTable({ title, rows, bear = false }) {
  if (!rows?.length) return null;
  return (
    <div className="qd-signal-table-wrap">
      <div className="qd-signal-table-title">{title}</div>
      <table className="qd-signal-table">
        <thead>
          <tr>
            <th>Stock</th>
            <th>Signal</th>
            <th>Score</th>
            <th>Chg%</th>
            <th>Tag</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 8).map((s) => (
            <tr key={s.symbol || s.stock}>
              <td>{(s.symbol || s.stock || '').replace('.NS', '')}</td>
              <td className={bear ? 'bear' : 'bull'}>{s.signal}</td>
              <td>{s.darvasScore ?? s.compositeScore ?? '—'}</td>
              <td className={(s.change ?? 0) >= 0 ? 'up' : 'down'}>
                {s.change != null ? `${s.change >= 0 ? '+' : ''}${Number(s.change).toFixed(2)}%` : '—'}
              </td>
              <td><em>{s.strategyTag || '—'}</em></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function QuantDashboard({
  mode = 'normal',
  analytics,
  sessionPhase,
  quant,
  niftyChange = 0,
  topPicks = [],
  topBearPicks = [],
  stockAnalysis = [],
  strategy = '',
  optionsQuant = null
}) {
  const [historySummary, setHistorySummary] = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    const url = mode === 'normal' ? '/api/normal/daily/history' : '/api/daily/history?limit=30';
    axios.get(url).then((res) => {
      const hist = res.data?.history || [];
      let peak = 0;
      let maxDrawdown = 0;
      let cum = 0;
      const curve = [];
      const days = hist.slice(0, 30);
      for (let i = days.length - 1; i >= 0; i -= 1) {
        cum += Number(days[i].netPnl) || 0;
        curve.push({ date: days[i].date, cum: Math.round(cum * 100) / 100 });
        if (cum > peak) peak = cum;
        const dd = peak - cum;
        if (dd > maxDrawdown) maxDrawdown = dd;
      }
      const wins = days.filter((d) => (d.netPnl || 0) > 0).length;
      setHistorySummary({
        days: days.length,
        totalPnl: Math.round(cum * 100) / 100,
        winDays: wins,
        winDayPct: days.length ? Math.round((wins / days.length) * 1000) / 10 : 0,
        maxDrawdown: Math.round(maxDrawdown * 100) / 100,
        targetHitDays: days.filter((d) => d.targetHit).length,
        equityCurve: curve
      });
    }).catch(() => {});
  }, [mode]);

  const exp = analytics?.expectancy || quant?.expectancy;

  return (
    <section className={`qd-panel ${collapsed ? 'collapsed' : ''}`}>
      <button type="button" className="qd-toggle" onClick={() => setCollapsed((v) => !v)}>
        <span>📐 Quantitative Trading Lab</span>
        <em>{collapsed ? 'Show' : 'Hide'}</em>
      </button>

      {!collapsed && (
        <div className="qd-body">
            {mode === 'normal' && (
            <p className="qd-action-link">Action screen shows only quant Δ picks (breakout + beats Nifty + BUY). Quant Lab below is the full scan with scores.</p>
          )}
          <div className="qd-regime">
            <div className="qd-regime-item">
              <span>Regime</span>
              <strong>{sessionPhase?.label || sessionPhase?.phase || '—'}</strong>
            </div>
            <div className="qd-regime-item">
              <span>Nifty</span>
              <strong className={(niftyChange ?? quant?.niftyChange ?? 0) >= 0 ? 'up' : 'down'}>
                {(niftyChange ?? quant?.niftyChange ?? 0) >= 0 ? '+' : ''}
                {Number(niftyChange ?? quant?.niftyChange ?? 0).toFixed(2)}%
              </strong>
            </div>
            {strategy && (
              <div className="qd-regime-item wide">
                <span>System</span>
                <strong>{strategy}</strong>
              </div>
            )}
          </div>

          {exp && (
            <div className="qd-metrics">
              <div className="qd-metric">
                <span>Expectancy / trade</span>
                <strong className={exp.expectancy >= 0 ? 'up' : 'down'}>₹{exp.expectancy}</strong>
              </div>
              <div className="qd-metric">
                <span>Profit factor</span>
                <strong>{exp.profitFactor}</strong>
              </div>
              <div className="qd-metric">
                <span>Risk : Reward</span>
                <strong>1 : {exp.riskReward}</strong>
              </div>
              <div className="qd-metric">
                <span>Break-even win %</span>
                <strong>{exp.breakEvenWinPct}%</strong>
              </div>
              <div className="qd-metric">
                <span>Win rate today</span>
                <strong>{analytics?.winRate ?? '—'}%</strong>
              </div>
              <div className="qd-metric">
                <span>Edge</span>
                <strong className={exp.edgePositive ? 'up' : 'down'}>{exp.edgePositive ? 'Positive' : 'Negative'}</strong>
              </div>
            </div>
          )}

          {mode === 'normal' && quant && (
            <div className="qd-exposure">
              <div className="qd-exposure-head">Portfolio exposure (10-slot basket)</div>
              <div className="qd-exposure-grid">
                <div><span>Deployed</span><strong>₹{quant.deployed}</strong></div>
                <div><span>Pool used</span><strong>{quant.poolUtilPct}%</strong></div>
                <div><span>Basket Δ%</span><strong className={quant.portfolioDeltaPct >= 0 ? 'up' : 'down'}>{quant.portfolioDeltaPct >= 0 ? '+' : ''}{quant.portfolioDeltaPct}%</strong></div>
                <div><span>vs Nifty β</span><strong>{quant.niftyBeta ?? '—'}</strong></div>
                <div><span>Est. ₹ if Nifty +1%</span><strong className="up">₹{quant.estPnLPerNifty1Pct ?? '—'}</strong></div>
                <div><span>Breakouts scanned</span><strong>{quant.signalCounts?.breakout ?? 0}</strong></div>
              </div>
              {quant.holdings?.length > 0 && (
                <div className="qd-holdings-chips">
                  {quant.holdings.map((h) => (
                    <span key={h.stock} className={`qd-h-chip ${h.side === 'SHORT' ? 'short' : 'long'}`}>
                      {h.stock} {h.weight}% · {h.change >= 0 ? '+' : ''}{h.change}%
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {mode === 'options' && optionsQuant?.hasPosition && (
            <div className="qd-exposure delta">
              <div className="qd-exposure-head">Options delta model (paper)</div>
              <div className="qd-exposure-grid">
                <div><span>Type</span><strong>{optionsQuant.optionType} @ {optionsQuant.strike}</strong></div>
                <div><span>Delta</span><strong>{optionsQuant.delta}</strong></div>
                <div><span>Eff. Δ</span><strong>{optionsQuant.effectiveDelta}</strong></div>
                <div><span>Spot move</span><strong>{optionsQuant.spotMove >= 0 ? '+' : ''}{optionsQuant.spotMove}</strong></div>
                <div><span>₹ per 10pt Nifty</span><strong>₹{optionsQuant.pnlSensitivityPer10Pts}</strong></div>
                <div><span>Est. premium Δ</span><strong>₹{optionsQuant.premiumMoveEst}</strong></div>
              </div>
              <p className="qd-delta-note">{optionsQuant.note}</p>
            </div>
          )}

          {historySummary && (
            <div className="qd-equity">
              <div className="qd-equity-head">
                <span>30-day equity curve</span>
                <strong className={historySummary.totalPnl >= 0 ? 'up' : 'down'}>
                  ₹{historySummary.totalPnl} · MDD ₹{historySummary.maxDrawdown}
                </strong>
              </div>
              <Sparkline points={historySummary.equityCurve} />
              <div className="qd-equity-stats">
                <span>{historySummary.winDays}/{historySummary.days} win days ({historySummary.winDayPct}%)</span>
                <span>{historySummary.targetHitDays} target hits</span>
              </div>
            </div>
          )}

          {mode === 'normal' && (
            <div className="qd-signals-row">
              <SignalTable title="▲ Bull signals (systematic)" rows={topPicks.length ? topPicks : stockAnalysis.filter((s) => (s.signal || '').includes('BUY')).slice(0, 8)} />
              <SignalTable title="▼ Bear signals" rows={topBearPicks} bear />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
